import type { TokenUserPayloadSerializer } from "@/types/member"

export const AUTH_MODE_KEY = "sigma:authMode"
export const AUTH_USER_KEY = "sigma:authUser"
export const WEB_CSRF_TOKEN_KEY = "sigma:webCsrfToken"
export const WEB_SESSION_EXPIRY_CHANGED_EVENT = "sigma-web-session-expiry-changed"
export const DEMO_MODE_KEY = "sigmaDemoMode"
export const DEMO_AUTH_TOKEN_KEY = "sigma:demoAuthToken"

const AUTH_STORAGE_KEYS = new Set([AUTH_MODE_KEY, AUTH_USER_KEY, WEB_CSRF_TOKEN_KEY, DEMO_MODE_KEY, DEMO_AUTH_TOKEN_KEY])

export type AuthMode = "web-session"

export interface WebSessionUserPayload {
  memberUuid: string
  memberId: string
  memberName: string
  memberProfileName?: string
  memberGrant: string
  isSuperuser: boolean
  memberLicenseType?: string
  memberLicenseNo?: string
}

interface PersistWebSessionAuthParams {
  user: WebSessionUserPayload
  expiresIn?: number
  csrfToken?: string
}

const getSessionExpiry = (expiresIn?: number) => {
  const fallbackSeconds = 60 * 60
  return Math.floor(Date.now() / 1000) + (expiresIn || fallbackSeconds)
}

const buildSessionPayloadWithExpiry = (user: TokenUserPayloadSerializer, expiresIn?: number): TokenUserPayloadSerializer => ({
  ...user,
  exp: getSessionExpiry(expiresIn),
})

export const toSessionMemberPayload = (user: WebSessionUserPayload, expiresIn?: number): TokenUserPayloadSerializer => ({
  exp: getSessionExpiry(expiresIn),
  memberUuid: user.memberUuid,
  isActive: true,
  isSuperuser: user.isSuperuser,
  memberGrant: user.memberGrant,
  memberName: user.memberName,
  memberProfileName: user.memberProfileName || "",
  memberLicenseType: user.memberLicenseType,
  memberLicenseNo: user.memberLicenseNo,
})

export const isWebSessionAuthMode = (): boolean => {
  if (typeof window === "undefined") {
    return false
  }

  return localStorage.getItem(AUTH_MODE_KEY) === "web-session"
}

export const getNativeWebSessionClient = (): "electron" | "capacitor" | null => {
  if (typeof window === "undefined") {
    return null
  }

  if (window.electronAPI) {
    return "electron"
  }

  if (__IS_CAPACITOR__) {
    return "capacitor"
  }

  return null
}

export const getCurrentAuthMode = (): AuthMode | null => {
  if (typeof window === "undefined") {
    return null
  }

  const mode = localStorage.getItem(AUTH_MODE_KEY)
  if (mode === "web-session") {
    return mode
  }

  return null
}

export const isAuthStorageEventKey = (key?: string | null): boolean => {
  if (key == null) {
    return true
  }

  return AUTH_STORAGE_KEYS.has(key)
}

export const getStoredWebSessionUser = (): TokenUserPayloadSerializer | null => {
  if (typeof window === "undefined") {
    return null
  }

  const rawValue = localStorage.getItem(AUTH_USER_KEY)
  if (!rawValue) {
    return null
  }

  try {
    return JSON.parse(rawValue) as TokenUserPayloadSerializer
  } catch (error) {
    console.warn("[webSessionAuth] 저장된 세션 사용자 정보 파싱 실패", error)
    return null
  }
}

export const getStoredWebCsrfToken = (): string | null => {
  if (typeof window === "undefined") {
    return null
  }

  return localStorage.getItem(WEB_CSRF_TOKEN_KEY)
}

export const updateStoredWebCsrfToken = (csrfToken?: string | null) => {
  if (typeof window === "undefined") {
    return
  }

  if (!csrfToken) {
    localStorage.removeItem(WEB_CSRF_TOKEN_KEY)
    return
  }

  localStorage.setItem(WEB_CSRF_TOKEN_KEY, csrfToken)
}

export const persistWebSessionAuth = ({ user, expiresIn, csrfToken }: PersistWebSessionAuthParams) => {
  if (typeof window === "undefined") {
    return
  }

  localStorage.setItem(AUTH_MODE_KEY, "web-session")
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(toSessionMemberPayload(user, expiresIn)))
  updateStoredWebCsrfToken(csrfToken)
}

export const refreshStoredWebSessionExpiry = (expiresIn?: number) => {
  if (typeof window === "undefined") {
    return
  }

  if (typeof expiresIn !== "number" || !Number.isFinite(expiresIn) || expiresIn <= 0) {
    return
  }

  const currentUser = getStoredWebSessionUser()
  if (!currentUser) {
    return
  }

  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(buildSessionPayloadWithExpiry(currentUser, expiresIn)))
  window.dispatchEvent(new Event(WEB_SESSION_EXPIRY_CHANGED_EVENT))
}

export const clearStoredAuthState = () => {
  if (typeof window === "undefined") {
    return
  }

  localStorage.removeItem(AUTH_MODE_KEY)
  localStorage.removeItem(AUTH_USER_KEY)
  localStorage.removeItem(WEB_CSRF_TOKEN_KEY)
  localStorage.removeItem(DEMO_MODE_KEY)
  localStorage.removeItem(DEMO_AUTH_TOKEN_KEY)
}

export const dispatchAuthStorageEvent = () => {
  if (typeof window === "undefined") {
    return
  }

  window.dispatchEvent(new Event("storage"))
}
